import React, { useState, useEffect, useRef, memo, useCallback } from 'react';

const PdfDropdown = memo(({ page, inputId, value, onChange, options = [], place_holder, multiple_options = false, onOpen, onClose }) => {
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedValues, setSelectedValues] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: '100%', bottom: 'auto' });
  const dropdownRef = useRef(null);

  // For multiple options, use the exact same logic as MultiSelectDropdown
  useEffect(() => {
    if (multiple_options) {
      // Initialize selected options from value prop - exact same logic as MultiSelectDropdown
      if (Array.isArray(value)) {
        setSelectedValues(value);
      } else if (typeof value === 'string' && value) {
        // Handle case where value might be a string
        setSelectedValues([value]);
      } else {
        setSelectedValues([]);
      }
    } else {
      // For single select, sync with value prop
      setSelectedValue(value || '');
    }
  }, [value, multiple_options]);

  // Handle click outside to close dropdown - same as MultiSelectDropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        if (onClose) onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const calculateDropdownPosition = useCallback(() => {
    if (!dropdownRef.current) return;

    const rect = dropdownRef.current.getBoundingClientRect();
    const dropdownHeight = 200; // Approximate height of dropdown
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      // Not enough space below, but enough space above - position above
      setDropdownPosition({ top: 'auto', bottom: '100%' });
    } else {
      // Default position below
      setDropdownPosition({ top: '100%', bottom: 'auto' });
    }
  }, []);

  // Exact same logic as MultiSelectDropdown's handleOptionChange
  const handleOptionToggle = (optionValue) => {
    const newSelectedOptions = selectedValues.includes(optionValue)
      ? selectedValues.filter((item) => item !== optionValue)
      : [...selectedValues, optionValue];

    setSelectedValues(newSelectedOptions);
    onChange(page, inputId, newSelectedOptions);
  };

  // Exact same logic as MultiSelectDropdown's handleSelectAll
  const handleSelectAll = () => {
    const allOptionValues = options.map(option => option.value || option);
    if (selectedValues.length === allOptionValues.length) {
      // Deselect all
      const newSelectedOptions = [];
      setSelectedValues(newSelectedOptions);
      onChange(page, inputId, newSelectedOptions);
    } else {
      // Select all
      const newSelectedOptions = [...allOptionValues];
      setSelectedValues(newSelectedOptions);
      onChange(page, inputId, newSelectedOptions);
    }
  };

  // Exact same logic as MultiSelectDropdown's getDisplayText
  const getSelectedText = () => {
    if (multiple_options) {
      // Ensure placeholder is always a string, never an array
      const placeholderText = (Array.isArray(place_holder) ? 'Select an option' : place_holder) || 'Select an option';
      
      if (selectedValues.length === 0) {
        return placeholderText;
      } else if (selectedValues.length === 1) {
        // For single selection, try to find the option label, otherwise use the value
        const option = options.find(opt => (opt.value || opt) === selectedValues[0]);
        return option ? (option.label || option) : selectedValues[0];
      } else {
        return `${selectedValues.length} options selected`;
      }
    } else {
      // Single select mode
      const currentValue = selectedValue || value;
      if (currentValue) {
        const selectedOption = options.find(option => (option.value || option) === currentValue);
        return selectedOption ? (selectedOption.label || selectedOption) : currentValue;
      } else {
        return place_holder || 'Select an option';
      }
    }
  };

  // Exact same logic as MultiSelectDropdown's handleToggleDropdown
  const handleToggleDropdown = () => {
    const newDropdownOpen = !dropdownOpen;
    setDropdownOpen(newDropdownOpen);
    if (newDropdownOpen) {
      calculateDropdownPosition();
      if (onOpen) onOpen();
    } else {
      if (onClose) onClose();
    }
  };

  const hasValue = multiple_options ? 
    (() => {
      const selectedText = getSelectedText();
      const placeholderText = (Array.isArray(place_holder) ? 'Select an option' : place_holder) || 'Select an option';
      return selectedValues.length > 0 && 
             selectedText !== 'Select an option' && 
             selectedText !== placeholderText &&
             selectedText !== '';
    })() : 
    !!(selectedValue || value);

  if (multiple_options) {
    return (
      <div className="pdf-dropdown pdf-dropdown-multiple" ref={dropdownRef}>
        <div className={`dropdown-wrapper ${hasValue ? 'has-value' : ''}`}>
          <div
            className="dropdown-header"
            onClick={handleToggleDropdown}
          >
            <span>{getSelectedText()}</span>
            <span className="dropdown-arrow">
              {dropdownOpen ? '▲' : '▼'}
            </span>
          </div>

          {dropdownOpen && (
            <div 
              className="dropdown-content"
              style={{
                top: dropdownPosition.top,
                bottom: dropdownPosition.bottom
              }}
            >
              <div
                className="select-all-option"
                onClick={handleSelectAll}
              >
                {selectedValues.length === options.length
                  ? 'De-select All'
                  : 'Select All'}
              </div>

              {options.map((option, index) => {
                const optionValue = option.value || option;
                const optionLabel = option.label || option;
                return (
                  <div key={index} className="option-item">
                    <input
                      type="checkbox"
                      id={`option-${inputId}-${index}`}
                      checked={selectedValues.includes(optionValue)}
                      onChange={() => handleOptionToggle(optionValue)}
                    />
                    <label htmlFor={`option-${inputId}-${index}`}>
                      {optionLabel}
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Single select - use same UI but without checkboxes and select all
  return (
    <div className="pdf-dropdown pdf-dropdown-multiple" ref={dropdownRef}>
      <div className={`dropdown-wrapper ${hasValue ? 'has-value' : ''}`}>
        <div
          className="dropdown-header"
          onClick={() => {
              const newDropdownOpen = !dropdownOpen;
              setDropdownOpen(newDropdownOpen);
              if (newDropdownOpen) {
                calculateDropdownPosition();
                if (onOpen) onOpen();
              } else {
                if (onClose) onClose();
              }
            }}
        >
          <span>{getSelectedText()}</span>
          <span className="dropdown-arrow">
            {dropdownOpen ? '▲' : '▼'}
          </span>
        </div>

        {dropdownOpen && (
          <div 
            className="dropdown-content"
            style={{
              top: dropdownPosition.top,
              bottom: dropdownPosition.bottom
            }}
          >
            {/* Dynamic placeholder option */}
            <div className={`option-item ${!selectedValue ? 'selected' : ''}`}>
              <label 
                htmlFor={`option-${inputId}-placeholder`}
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedValue('');
                  onChange(page, inputId, '');
                  setDropdownOpen(false);
                  if (onClose) onClose();
                }}
              >
                {place_holder || 'Select an option'}
              </label>
            </div>
            
            {options.map((option, index) => {
              const optionValue = option.value || option;
              const optionLabel = option.label || option;
              const isSelected = selectedValue === optionValue;
              return (
                <div key={index} className={`option-item ${isSelected ? 'selected' : ''}`}>
                  <label 
                    htmlFor={`option-${inputId}-${index + 1}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedValue(optionValue);
                      onChange(page, inputId, optionValue);
                      setDropdownOpen(false);
                      if (onClose) onClose();
                    }}
                  >
                    {optionLabel}
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

export default PdfDropdown;
